import { useTranslation } from "react-i18next";
import { useInfiniteQuery, QueryKey, InfiniteData } from "@tanstack/react-query";

import { ActivityType, getUserActivities, UserActivitiesResponse } from "../services/activities.service";
import { userKeys } from "../queryKeys";
import { Post } from "../../../Feeds/types/post.type";

type QueryFnData = UserActivitiesResponse;
type PageParam = number;

interface TransformedData extends InfiniteData<QueryFnData, PageParam> {
  posts: Post[];
  mentionedUsers: number[];
}

export const useUserActivities = (userId: string | null, activityType: ActivityType = ActivityType.all) => {
  const { i18n } = useTranslation();
  return useInfiniteQuery<QueryFnData, Error, TransformedData, QueryKey, PageParam>({
    queryKey: [...userKeys.activities(userId!, activityType), i18n.language, activityType],
    queryFn: async ({ pageParam }) => {
      if (!userId) throw new Error("User ID is required");
      return getUserActivities(userId, pageParam, activityType);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: QueryFnData, allPages: QueryFnData[]) => {
      if (lastPage.items.length === 0) {
        return undefined;
      }

      const currentOffset = allPages.reduce((acc: number, page: QueryFnData) => acc + page.items.length, 0);
      const totalItems = lastPage.total;

      if (currentOffset < totalItems) {
        return currentOffset;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => ({
      ...data,
      posts: data.pages.flatMap((page) => page.items) || [],
      mentionedUsers: Array.from(new Set(data.pages.flatMap((page) => page.mentionedUsers))),
    }),
    enabled: !!userId,
  });
};
